import React from 'react';
import PropTypes from 'prop-types';
import View from "react-native-web/dist/exports/View";
import { useThemeTokens, useThemeTokensCallback, useResponsiveThemeTokens, useTheme } from '../ThemeProvider';
import { getTokensPropType, variantProp, StyleSheet, createMediaQueryStyles } from '../utils';
import { useViewport } from '../ViewportProvider';
import { a11yProps, linkProps, selectSystemProps, viewProps, responsiveProps } from '../utils/props';
import CardBase from './CardBase';
import PressableCardBase from './PressableCardBase';
import CheckboxButton from '../Checkbox/CheckboxButton';
import RadioButton from '../Radio/RadioButton';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps, linkProps]);
const SelectionType = {
  Checkbox: 'checkbox',
  Radio: 'radiogroup',
  None: undefined
};
const selectInputStyle = _ref => {
  let {
    paddingTop,
    paddingRight
  } = _ref;
  return {
    position: 'absolute',
    top: paddingTop,
    right: paddingRight
  };
};
const getInputProps = _ref2 => {
  let {
    id,
    checkColor,
    boxBackgroundColor,
    checkBackgroundColor,
    isControlled,
    isChecked,
    isInactive,
    handleChange
  } = _ref2;
  return {
    inputId: id,
    tokens: {
      iconColor: checkColor,
      inputBackgroundColor: boxBackgroundColor,
      iconBackgroundColor: checkBackgroundColor
    },
    isControlled,
    isChecked,
    inactive: isInactive,
    handleChange
  };
};

/**
 * A basic card component, unstyled by default.
 *
 * ## Component API
 *
 * ### Background
 *
 * In order to control the background of a card, the following tokens are currently
 * supported:
 *
 * - `backgroundColor`
 *
 * ### Border
 *
 * The following border tokens can be used:
 *
 * - `borderColor`
 * - `borderRadius`
 * - `borderWidth`
 *
 * ### Padding
 *
 * Please use the following tokens to control the padding:
 *
 * - `paddingBottom`
 * - `paddingLeft`
 * - `paddingRight`
 * - `paddingTop`
 *
 * Note that various viewport sizes are supported as well.
 *
 * ### Shadow
 *
 * Feel free to use the following properties within the `shadow` token:
 *
 * - `inset`: boolean
 * - `color`: string
 * - `offsetX`: number
 * - `offsetY`: number
 * - `blur`: number
 * - `spread`: number
 *
 * ## Accessibility
 *
 * Card supports all the common a11y props. Please remember that by marking a card as `accessible`
 * you automatically make inaccessible its children, which may or may not be appropriate
 * depending on what you are trying to achieve.
 */
const Card = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    children,
    tokens,
    variant,
    dataSet,
    onPress,
    id,
    interactiveCard,
    backgroundImage,
    ...rest
  } = _ref3;
  const viewport = useViewport();
  const {
    themeOptions
  } = useTheme();
  const {
    enableMediaQueryStyleSheet
  } = themeOptions;
  const useTokens = enableMediaQueryStyleSheet ? useResponsiveThemeTokens : useThemeTokens;
  const themeTokens = useTokens('Card', tokens, variant, !enableMediaQueryStyleSheet && {
    viewport
  });
  const selected = interactiveCard?.variant?.selected;
  const inactive = interactiveCard?.variant?.inactive;
  const selectionType = interactiveCard?.selectionType;
  const getThemeTokens = useThemeTokensCallback('Card', interactiveCard?.tokens, {
    interactive: true,
    ...(interactiveCard?.variant || {})
  });
  // When interactive area is present, spacing tokens should only be applied
  // to individual Card sections, not Card as a whole
  const {
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    ...restOfTokens
  } = selectionType ? getThemeTokens() : themeTokens;
  const {
    inputBackgroundColor,
    iconBackgroundColor,
    iconColor,
    icon,
    inputBorderRadius,
    inputHeight,
    inputWidth,
    iconSize,
    inputShadow,
    inputBorderWidth,
    inputBorderColor
  } = getThemeTokens();
  const checkboxTokens = {
    inputBackgroundColor,
    iconBackgroundColor,
    icon,
    iconColor,
    inputBorderRadius,
    inputHeight,
    inputWidth,
    iconSize,
    inputShadow,
    inputBorderWidth,
    inputBorderColor
  };
  const radioTokens = {
    iconBackgroundColor,
    iconColor,
    containerShadow: inputShadow,
    icon,
    inputBackgroundColor,
    inputBorderRadius,
    inputSize: inputHeight,
    checkedSize: iconSize,
    inputBorderWidth,
    inputBorderColor
  };
  let cardStyles;
  let mediaIds;
  if (enableMediaQueryStyleSheet) {
    const mediaQueryStyleSheet = createMediaQueryStyles(themeTokens);
    const {
      ids,
      styles
    } = StyleSheet.create({
      card: mediaQueryStyleSheet
    });
    cardStyles = styles.card;
    mediaIds = ids.card;
  } else {
    cardStyles = themeTokens;
  }
  const renderInputPerSelectionType = props => {
    switch (selectionType) {
      case SelectionType.Checkbox:
        return /*#__PURE__*/_jsx(View, {
          style: selectInputStyle(getThemeTokens()),
          children: /*#__PURE__*/_jsx(CheckboxButton, {
            ...props,
            tokens: {
              ...checkboxTokens,
              ...props?.tokens
            }
          })
        });
      case SelectionType.Radio:
        return /*#__PURE__*/_jsx(View, {
          style: selectInputStyle(getThemeTokens()),
          children: /*#__PURE__*/_jsx(RadioButton, {
            ...props,
            tokens: {
              ...radioTokens,
              ...props?.tokens
            }
          })
        });
      default:
        return null;
    }
  };
  const renderNoSelectionView = () => /*#__PURE__*/_jsx(View, {
    style: {
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight
    },
    children: children
  });
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(CardBase, {
      ref: ref,
      tokens: interactiveCard?.body ? restOfTokens : cardStyles,
      backgroundImage: backgroundImage,
      dataSet: mediaIds && {
        media: mediaIds
      },
      ...selectProps(rest),
      children: interactiveCard?.body ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(PressableCardBase, {
          ref: ref,
          tokens: getThemeTokens,
          dataSet: dataSet,
          onPress: onPress,
          ...selectProps(rest),
          children: cardState => {
            const {
              iconColor: checkColor,
              inputBackgroundColor: boxBackgroundColor,
              iconBackgroundColor: checkBackgroundColor
            } = getThemeTokens({
              ...cardState,
              selected,
              interactive: true,
              isControl: true
            }, interactiveCard?.tokens);
            return /*#__PURE__*/_jsxs(_Fragment, {
              children: [renderInputPerSelectionType(getInputProps({
                id,
                checkColor,
                boxBackgroundColor,
                checkBackgroundColor,
                isControlled: true,
                isChecked: selected || cardState?.hover,
                isInactive: inactive,
                onPress
              })), typeof interactiveCard?.body === 'function' ? interactiveCard.body(cardState) : interactiveCard.body]
            });
          }
        }), children && selectionType !== SelectionType.None ? renderNoSelectionView() : null]
      }) : children
    })
  });
});
Card.displayName = 'Card';
Card.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Card content.
   */
  children: PropTypes.node,
  /**
   * Card tokens.
   */
  tokens: getTokensPropType('Card'),
  /**
   * Card variant.
   */
  variant: variantProp.propType,
  /**
   * Function to call on pressing the card.
   * Note: This is only available when `interactiveCard` prop is used.
   */
  onPress: PropTypes.func,
  /**
   * Card ID.
   */
  id: PropTypes.string,
  /**
   * Object to set interactive card's properties
   * - body: The body of the interactive card, can be any renderable node
   * - tokens: The tokens to be used for the interactive card
   * - selectionType: If the card has `isControl` variant set to `true`, the selectionType can have one of the following options: 
        - `radiogroup`
        - `checkbox`
   * - variant: The variant to be used for the interactive card
   */
  interactiveCard: PropTypes.shape({
    body: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    tokens: getTokensPropType('Card'),
    selectionType: PropTypes.oneOf(Object.values(SelectionType)),
    variant: variantProp.propType
  }),
  /**
   * Apply background image to the card.
   */
  backgroundImage: PropTypes.shape({
    // The image src is either a URI string or a number (when a local image src is bundled in IOS or Android app)
    // src is an object when used responsively to provide different image sources for different screen sizes
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]).isRequired,
    alt: PropTypes.string,
    resizeMode: responsiveProps.getTypeOptionallyByViewport(PropTypes.oneOf(['cover', 'contain', 'stretch', 'repeat', 'center']))
  })
};
export default Card;